/**
 * 表 1 / 表 2：分类别指标（单 job 视图为单列，对比视图为「基线 / 本次 / Δ」三列组）。
 *
 * ``ALL`` 行是算法侧上报的合计行，与各真实类别**并列**存放且已是合计值——所以它在
 * 后端排序里被置底、这里加底纹，避免被当成又一个类别读。注意它未必等于各类别之和
 * （存在未分类目标时会有差），以算法侧的 ALL 为准。
 *
 * IoU 列按计数单位切换：``cell``（OCC 格子）为「IoU」，其余（``box``）为「平均 IoU」。
 * 行里带扩展计数 ``fp_unlabeled`` 时（OCC）多一列「未标注区」：仿真给出该类、真值无标注
 * 的格子，仅作参考、不计入精确率。
 */
import type { ReactNode } from 'react';
import { Table, Tooltip } from 'antd';
import type { TableProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import type {
  PerceptionCategoryCompareRow,
  PerceptionCategoryMetrics,
} from '@/apis/simulation';

import { deltaColor, fmtCount, fmtCountDelta, fmtPp, fmtRatio, isCellUnit } from '../format';

/** 类别展示：中文名 + 括号英文原文。中文名由后端随行下发，未登记则回退英文。 */
const categoryLabel = (category: string, cn: string | null): string =>
  cn && cn !== category ? `${cn}(${category})` : category;

const ALL_CATEGORY = 'ALL';

const rowClass = (category: string): string =>
  category === ALL_CATEGORY ? 'pe-row-all' : '';

/**
 * 行展开配置（OD 报告用）：传了 ``renderExpanded`` 才启用，每个类别行左侧出现 ▶，
 * 展开内容由调用方渲染（该类别下的逐 case 明细）。``hasExpanded`` 为 false 的类别不可展开。
 */
export interface CategoryExpandProps {
  renderExpanded?: (category: string) => ReactNode;
  hasExpanded?: (category: string) => boolean;
}

function buildExpandable<T extends { category: string }>({
  renderExpanded,
  hasExpanded,
}: CategoryExpandProps): TableProps<T>['expandable'] {
  if (!renderExpanded) {
    return undefined;
  }
  return {
    expandedRowRender: (r) => renderExpanded(r.category),
    rowExpandable: (r) => (hasExpanded ? hasExpanded(r.category) : true),
  };
}

// ── 单 job 视图 ──────────────────────────────────────────────────────────────

const FP_UNLABELED = 'fp_unlabeled';

const buildSingleColumns = (
  unit: string | null | undefined,
  showUnlabeled: boolean,
): ColumnsType<PerceptionCategoryMetrics> => [
  {
    title: '类别',
    dataIndex: 'category',
    key: 'category',
    width: 170,
    render: (_, r) => categoryLabel(r.category, r.categoryCn),
  },
  { title: 'GT', dataIndex: 'gt', key: 'gt', width: 90, align: 'right', render: fmtCount },
  { title: '检出', dataIndex: 'det', key: 'det', width: 90, align: 'right', render: fmtCount },
  { title: 'TP', dataIndex: 'tp', key: 'tp', width: 90, align: 'right', render: fmtCount },
  { title: 'FN', dataIndex: 'fn', key: 'fn', width: 90, align: 'right', render: fmtCount },
  { title: 'FP', dataIndex: 'fp', key: 'fp', width: 90, align: 'right', render: fmtCount },
  ...(showUnlabeled
    ? [
        {
          title: (
            <Tooltip title="仿真给出该类、真值无标注的格子；仅作参考，不计入精确率">未标注区</Tooltip>
          ),
          key: FP_UNLABELED,
          width: 100,
          align: 'right' as const,
          render: (_: unknown, r: PerceptionCategoryMetrics) => (
            <span style={{ color: '#9b9da3' }}>{fmtCount(r.extraCounts?.[FP_UNLABELED])}</span>
          ),
        },
      ]
    : []),
  {
    title: '召回率',
    dataIndex: 'recall',
    key: 'recall',
    width: 100,
    align: 'right',
    sorter: (a, b) => (a.recall ?? -1) - (b.recall ?? -1),
    render: (v) => fmtRatio(v),
  },
  {
    title: (
      <Tooltip title="分类别行：按位置配对成功的目标中，类别判断正确的比例。配对数独立计算，可能与检出/TP 不同。ALL 行仍为检测精确率；无样本或统计不可用时显示 —。">
        <span>精确率</span>
      </Tooltip>
    ),
    dataIndex: 'precision',
    key: 'precision',
    width: 100,
    align: 'right',
    render: (v) => fmtRatio(v),
  },
  { title: 'F1', dataIndex: 'f1', key: 'f1', width: 90, align: 'right', render: (v) => fmtRatio(v) },
  isCellUnit(unit)
    ? {
        title: <Tooltip title="Σtp/(Σtp+Σfp+Σfn)">IoU</Tooltip>,
        dataIndex: 'iou',
        key: 'iou',
        width: 100,
        align: 'right',
        render: (v) => fmtRatio(v),
      }
    : {
        title: <Tooltip title="Σiou_sum/Σtp">平均 IoU</Tooltip>,
        dataIndex: 'meanIou',
        key: 'meanIou',
        width: 100,
        align: 'right',
        render: (v) => fmtRatio(v),
      },
  {
    title: 'case 数',
    dataIndex: 'caseCount',
    key: 'caseCount',
    width: 90,
    align: 'right',
    render: fmtCount,
  },
];

export function SingleCategoryTable({
  rows,
  unit,
  renderExpanded,
  hasExpanded,
}: {
  rows: PerceptionCategoryMetrics[];
  /** 计数单位：决定 IoU 列口径；不传按 box（OD）处理 */
  unit?: string | null;
} & CategoryExpandProps) {
  const showUnlabeled = rows.some((r) => r.extraCounts?.[FP_UNLABELED] !== undefined);
  return (
    <Table<PerceptionCategoryMetrics>
      rowKey="category"
      size="small"
      dataSource={rows}
      columns={buildSingleColumns(unit, showUnlabeled)}
      pagination={false}
      tableLayout="fixed"
      scroll={{ x: showUnlabeled ? 1040 : 940 }}
      rowClassName={(r) => rowClass(r.category)}
      expandable={buildExpandable({ renderExpanded, hasExpanded })}
    />
  );
}

// ── 对比视图 ────────────────────────────────────────────────────────────────

const compareColumns: ColumnsType<PerceptionCategoryCompareRow> = [
  {
    title: '类别',
    dataIndex: 'category',
    key: 'category',
    width: 170,
    fixed: 'left',
    render: (_, r) => categoryLabel(r.category, r.categoryCn),
  },
  {
    title: '基线',
    children: [
      { title: 'GT', key: 'bGt', width: 90, align: 'right', render: (_, r) => fmtCount(r.base?.gt) },
      { title: 'TP', key: 'bTp', width: 90, align: 'right', render: (_, r) => fmtCount(r.base?.tp) },
      {
        title: '召回率',
        key: 'bRecall',
        width: 100,
        align: 'right',
        render: (_, r) => fmtRatio(r.base?.recall),
      },
    ],
  },
  {
    title: '本次',
    children: [
      { title: 'GT', key: 'cGt', width: 90, align: 'right', render: (_, r) => fmtCount(r.current?.gt) },
      { title: 'TP', key: 'cTp', width: 90, align: 'right', render: (_, r) => fmtCount(r.current?.tp) },
      {
        title: '召回率',
        key: 'cRecall',
        width: 100,
        align: 'right',
        render: (_, r) => fmtRatio(r.current?.recall),
      },
    ],
  },
  {
    title: (
      <Tooltip title="计数为差值；召回率为百分点差（pp）。任一侧缺该类别则为 —（不是 0）">
        Δ
      </Tooltip>
    ),
    children: [
      {
        title: 'GT',
        key: 'dGt',
        width: 90,
        align: 'right',
        render: (_, r) => (
          <span style={{ color: deltaColor(r.delta?.gt) }}>{fmtCountDelta(r.delta?.gt)}</span>
        ),
      },
      {
        title: 'TP',
        key: 'dTp',
        width: 90,
        align: 'right',
        render: (_, r) => (
          <span style={{ color: deltaColor(r.delta?.tp) }}>{fmtCountDelta(r.delta?.tp)}</span>
        ),
      },
      {
        title: '召回率',
        key: 'dRecall',
        width: 110,
        align: 'right',
        sorter: (a, b) => (a.delta?.recall ?? 0) - (b.delta?.recall ?? 0),
        render: (_, r) => (
          <span style={{ color: deltaColor(r.delta?.recall) }}>{fmtPp(r.delta?.recall)}</span>
        ),
      },
    ],
  },
];

export function CompareCategoryTable({
  rows,
  renderExpanded,
  hasExpanded,
}: { rows: PerceptionCategoryCompareRow[] } & CategoryExpandProps) {
  return (
    <Table<PerceptionCategoryCompareRow>
      rowKey="category"
      size="small"
      dataSource={rows}
      columns={compareColumns}
      pagination={false}
      tableLayout="fixed"
      scroll={{ x: 1000 }}
      rowClassName={(r) => rowClass(r.category)}
      expandable={buildExpandable({ renderExpanded, hasExpanded })}
    />
  );
}
